import React, { useState, useRef, useEffect } from 'react'
import { MessageCircle, X, Send, Bot, Wifi, WifiOff } from 'lucide-react'
import { ChatMessage } from '../types'

interface ChatWidgetProps {
  apiUrl?: string  // Made optional since we're using workers.dev directly
}

const ChatWidget: React.FC<ChatWidgetProps> = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content: "Hi! I'm your AI shopping assistant. How can I help you find products today?",
      timestamp: Date.now()
    }
  ])
  const [input, setInput] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [socket, setSocket] = useState<WebSocket | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen && !socket) {
      connectWebSocket()
    }
    return () => {
      if (socket) {
        socket.close()
      }
    }
  }, [isOpen])

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const connectWebSocket = () => {
    setConnectionError(null)
    // Use workers.dev directly to bypass Pages custom domain conflict
    const wsUrl = 'wss://cf-one-demo-chat.rob-danz.workers.dev/chat'
    
    try {
      const ws = new WebSocket(wsUrl)

      ws.onopen = () => {
        console.log('Chat connected')
        setIsConnected(true)
        setConnectionError(null)
      }

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          if (data.type === 'message') {
            setMessages(prev => [...prev, {
              id: `msg-${Date.now()}`,
              role: 'assistant',
              content: data.content,
              timestamp: Date.now()
            }])
            setIsTyping(false)
          }
        } catch (err) {
          console.error('Error parsing message:', err)
        }
      }

      ws.onerror = (error) => {
        console.error('WebSocket error:', error)
        setConnectionError('Connection error')
        setIsConnected(false)
      }

      ws.onclose = () => {
        console.log('Chat disconnected')
        setIsConnected(false)
        setSocket(null)
      }

      setSocket(ws)
    } catch (err) {
      console.error('Failed to create WebSocket:', err)
      setConnectionError('Failed to connect')
    }
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const sendMessage = () => {
    if (!input.trim() || !socket || socket.readyState !== WebSocket.OPEN) return

    const message: ChatMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: input,
      timestamp: Date.now()
    }

    setMessages(prev => [...prev, message])
    setInput('')
    setIsTyping(true)

    socket.send(JSON.stringify({
      type: 'message',
      message: input
    }))
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const reconnect = () => {
    if (socket) {
      socket.close()
    }
    connectWebSocket()
  }

  return (
    <>
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-4 right-4 bg-orange-500 text-white p-4 rounded-full shadow-lg hover:bg-orange-600 transition-all z-50"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {isOpen && (
        <div className="fixed bottom-4 right-4 w-96 h-[500px] bg-white rounded-2xl shadow-2xl flex flex-col z-50 animate-fade-in">
          <div className="flex items-center justify-between p-4 border-b bg-orange-500 text-white rounded-t-2xl">
            <div className="flex items-center space-x-2">
              <Bot className="h-5 w-5" />
              <div>
                <h3 className="font-semibold">AI Assistant</h3>
                <p className="text-xs text-orange-100 flex items-center gap-1">
                  {isConnected ? (
                    <><Wifi className="h-3 w-3" /> Connected</>
                  ) : (
                    <><WifiOff className="h-3 w-3" /> Disconnected</>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 hover:bg-orange-600 rounded transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {connectionError && (
            <div className="bg-red-50 border-b border-red-200 p-3 text-center">
              <p className="text-red-600 text-sm mb-2">{connectionError}</p>
              <button
                onClick={reconnect}
                className="text-orange-600 text-sm font-medium hover:underline"
              >
                Retry Connection
              </button>
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
            {messages.map(msg => (
              <div
                key={msg.id}
                className={`chat-message ${msg.role} px-4 py-2 rounded-2xl shadow-sm`}
              >
                {msg.content}
              </div>
            ))}
            {isTyping && (
              <div className="chat-message bot px-4 py-2 rounded-2xl flex items-center space-x-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="p-4 border-t bg-white rounded-b-2xl">
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={isConnected ? "Ask about products..." : "Connecting..."}
                disabled={!isConnected}
                className="flex-1 px-4 py-2 border rounded-full focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || !isConnected}
                className="p-2 bg-orange-500 text-white rounded-full hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Send className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ChatWidget
